import { useCallback } from 'react';
import React,{ useEffect,useState } from 'react';
import { DosarContext,type CaseData,type DosarProviderProps,type Folder,type ToastMessage } from './DosarContext.shared';

import { getSharedWithMeDetails } from '../lib/api';
import { crmStorageService } from '../services/crmStorageService';
import { useAuth } from './AuthContext.shared';

export const DosarProvider: React.FC<DosarProviderProps> = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const [items, setItems] = useState<CaseData[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [foldersSharedWithMe, setFoldersSharedWithMe] = useState<Folder[]>([]);
    const [currentFolderId, setCurrentFolderId] = useState<number | null>(null); // null = Root (or All, depending on view)

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Initial Load


    // Reload items when changing folder
    useEffect(() => {
        if (isAuthenticated) {
            loadItems(currentFolderId);
        }
    }, [currentFolderId, isAuthenticated, user?.id]);

    const loadItems = async (folderId: number | null) => {
        try {
            const savedItems = await crmStorageService.getDosarItems(folderId);
            setItems(savedItems);
        } catch (e) {
            console.error("Failed to load items", e);
        }
    };

    const loadFolders = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const [personalFolders, shared] = await Promise.all([
                crmStorageService.getFolders(),
                getSharedWithMeDetails()
            ]);

            setFolders(personalFolders);

            // Transform shared info to Folder type
            const sharedFoldersMapped: Folder[] = shared.map(s => ({
                ...s.folder,
                is_shared: true,
                permission: s.permission,
                owner_name: s.owner_name
            }));
            setFoldersSharedWithMe(sharedFoldersMapped);

        } catch (e) {
            console.error("Failed to load folders", e);
        }
    }, [isAuthenticated])

useEffect(() => {
        const loadData = async () => {
            if (isAuthenticated) {
                try {
                    await loadFolders();
                } catch (e) {
                    console.error("Failed to load dosar data", e);
                }
            } else {
                // Local Storage Logic (Legacy/Simplified)
                const saved = localStorage.getItem('dosar_items') || sessionStorage.getItem('dosar_items');
                if (saved) {
                    try {
                        setItems(JSON.parse(saved));
                    } catch (e) {
                        console.error('Failed to parse dosar items', e);
                    }
                }
            }
        };

        loadData();
    }, [isAuthenticated, user?.id, loadFolders]);


    // Persistence Effect (Local Only)
    useEffect(() => {
        if (!isAuthenticated) {
            localStorage.setItem('dosar_items', JSON.stringify(items));
        }
    }, [items, isAuthenticated]);


    const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    }, []);



    const hideToast = () => setToast(null);

    const addToDosar = async (item: CaseData, folderId: number | null = null) => {
        // Validation: Check if already in current view/folder?
        // Or check globally?
        // Ideally we check if it is in the TARGET folder.
        // For simplicity, we trust the backend check mostly, but do a quick local check if displayed.

        if (items.some((i) => String(i.id) === String(item.id))) {
            // It might be in the list, but is it the same folder?
            // Since 'items' is now scoped to 'currentFolderId', checking against it is correct for duplicates in current view.
            // BUT if we add to a DIFFERENT folder than current view, 'items' check is irrelevant.
            // So:
            if (currentFolderId === folderId) {
                showToast('Această speță este deja în acest dosar.', 'info');
                return;
            }
        }

        if (items.length >= 10 && currentFolderId === folderId) {
            // Only block optimistic update if we are in the target folder and its full
            showToast('Dosarul este plin (maxim 10 spețe).', 'error');
            // We can let backend enforce it too, but UI feedback is nice.
            return;
        }

        // Optimistic UI Update (Only if adding to current folder)
        const previousItems = [...items];
        if (currentFolderId === folderId) {
            setItems((prev) => [item, ...prev]);
        }

        if (isAuthenticated) {
            try {
                await crmStorageService.addDosarItem({ ...item.data, id: item.id }, folderId);
                showToast('Speță salvată în dosar.', 'success');
                // Reload to sync ensure IDs correct etc
                if (currentFolderId === folderId) loadItems(folderId);
            } catch (e) {
                if (currentFolderId === folderId) setItems(previousItems); // Rollback
                showToast((e as Error).message || 'Eroare la salvarea în cont.', 'error');
            }
        } else {
            // Local mode doesn't support folders yet? Or we simulate a single "root" folder
            if (folderId !== null) {
                showToast('Folderele sunt disponibile doar pentru conturi.', 'info');
                return;
            }
            showToast('Speță adăugată la dosar (Local).', 'success');
        }
    };

    const removeFromDosar = async (id: number | string) => {
        const previousItems = [...items];
        setItems((prev) => prev.filter((i) => String(i.id) !== String(id)));

        if (isAuthenticated) {
            try {
                const vaultItemId = previousItems.find((item) => String(item.id) === String(id))?.vault_item_id;
                if (!vaultItemId) throw new Error('Identificatorul tehnic al speței lipsește.');
                await crmStorageService.removeDosarItem(vaultItemId);
                showToast('Speță ștearsă.', 'info');
            } catch  {
                setItems(previousItems); // Rollback
                showToast('Eroare la ștergere.', 'error');
            }
        } else {
            showToast('Speță ștearsă din dosar.', 'info');
        }
    };

    const isCaseInDosar = (id: number | string) => {
        // This checks if it's in the CURRENT viewing list.
        return items.some((i) => String(i.id) === String(id));
    };

    // Folder Operations
    const createNewFolder = async (nume: string) => {
        if (!isAuthenticated) return;
        try {
            await crmStorageService.createFolder(nume);
            loadFolders();
            showToast('Dosar creat cu succes.', 'success');
        } catch  {
            showToast('Eroare la crearea dosarului.', 'error');
        }
    };

    const updateFolder = async (id: number, data: { nume?: string; prompt_user_request?: string; prompt_fact_situation?: string }) => {
        try {
            await crmStorageService.updateFolder(id, data);
            loadFolders();
            if (data.nume) showToast('Dosar actualizat.', 'success');
        } catch  {
            showToast('Eroare la actualizare.', 'error');
        }
    };

    const removeFolder = async (id: number) => {
        try {
            await crmStorageService.deleteFolder(id);
            setFolders(prev => prev.filter(f => f.id !== id));
            // Also update shared? Assume we can't delete shared folders (only revoke or owner deletes)
            // But if we are deleting our OWN folder, it's in 'folders'
            if (currentFolderId === id) setCurrentFolderId(null); // Go to root
            showToast('Dosar șters.', 'success');
        } catch  {
            showToast('Eroare la ștergerea dosarului.', 'error');
        }
    }

    const openFolder = (id: number | null) => {
        setCurrentFolderId(id);
    }

    const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);

    return (
        <DosarContext.Provider
            value={{
                items,
                folders,
                foldersSharedWithMe,
                currentFolderId,
                addToDosar,
                removeFromDosar,
                isCaseInDosar,
                isDrawerOpen,
                toggleDrawer,
                loadFolders,
                createNewFolder,
                updateFolder,
                removeFolder,
                openFolder,
                toast,
                hideToast,
                showToast,
            }}
        >
            {children}
        </DosarContext.Provider>
    );
};
